import { PureComponent, ReactNode } from "react";
import { MusicNoteIdentity, MusicScale } from "../../../wasm/pkg/wasm";
import { buildNoteNameMap, ChordInScaleEdit, listAllNoteIds } from "../../models/wasm.model";
import { StringsTuning, TablatureSingleChord } from "./InstrumentTypes.model";
import "./StringInstrumentNeck.css";

interface Props {
  tabSingleChord: TablatureSingleChord;
  chord: ChordInScaleEdit;
  chordIndex: number; // convenience - tell parent which chord
  tuning: StringsTuning;
  capoPosition: number;
  scale?: MusicScale;
  onUpdateTabForSingleChord: (t: TablatureSingleChord, chordIndex: number) => void;
  onPromptUpdateChordMeta: (chordIndex: number) => void;
}
interface State {}

type NoteInclusion = "note-out-of-scale" | "note-in-scale-not-chord" | "note-in-chord-silent" | "note-in-chord-audible";

/** which notes are in the chord and scale */
function buildNoteInclusionMap(chord: ChordInScaleEdit, scale?: MusicScale): Record<MusicNoteIdentity, NoteInclusion> {
  const map = {} as Record<MusicNoteIdentity, NoteInclusion>;
  listAllNoteIds().forEach((nid) => {
    map[nid] = scale ? "note-out-of-scale" : "note-in-scale-not-chord"; // if there is no scale, everything is "in the scale"
  });
  if (scale) {
    scale.shape_notes.forEach((mn) => {
      map[mn.note_id] = "note-in-scale-not-chord";
    });
  }
  chord.silent_notes.forEach((n) => (map[n.note_id] = "note-in-chord-silent"));
  chord.notes.forEach((n) => (map[n.note_id] = "note-in-chord-audible"));
  return map;
}

class StringInstrumentNeck extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {};
  }

  renderSingleString(
    notesPerString: Array<MusicNoteIdentity>,
    stringIndex: number,
    noteInclusionMap: Record<MusicNoteIdentity, NoteInclusion>,
    noteNameMap: Record<MusicNoteIdentity, string>,
    capoPosition: number
  ): ReactNode {
    const {
      tabSingleChord: { stringFretsLowToHigh },
    } = this.props;
    const key = `string-${stringIndex}`;
    return (
      <div key={key} className="string-instrument-string">
        {notesPerString.map((nid, noteIndex) => {
          const inclusionCss = noteIndex < capoPosition ? 'note-unreachable-capo' : noteInclusionMap[nid];
          const isChosenCss = stringFretsLowToHigh[stringIndex] === noteIndex ? "note-chosen-for-tab" : "";
          const css = `string-instrument-note ${inclusionCss} ${isChosenCss}`;
          const noteName = noteNameMap[nid];
          const key = `string${stringIndex}-note${noteIndex}`;
          return (
            <div
              key={key}
              className={css}
              onClick={
                noteInclusionMap[nid] === "note-in-chord-audible" || noteInclusionMap[nid] === "note-in-chord-silent"
                  ? this.curryHandleValidTabIndexSelect(stringIndex, noteIndex, nid, noteInclusionMap)
                  : this.handleInvalidTabIndexSelect
              }
            >
              {inclusionCss === "note-out-of-scale" ? " " : noteName}
            </div>
          );
        })}
      </div>
    );
  }
  curryHandleValidTabIndexSelect =
    (stringIndex: number, noteIndex: number, noteId: MusicNoteIdentity, noteInclusionMap: Record<MusicNoteIdentity, NoteInclusion>) =>
    (_e: React.MouseEvent<HTMLDivElement>) => {
      const {
        tabSingleChord: { stringFretsLowToHigh },
      } = this.props;
      if (noteInclusionMap[noteId] === "note-in-chord-silent") {
        // console.log("silent note selected, adjusting chord");
        // prompt the user: are you sure you want to change the chord voicing?")
        this.props.onPromptUpdateChordMeta(this.props.chordIndex);
      } else {
        if (stringFretsLowToHigh[stringIndex] === noteIndex) {
          stringFretsLowToHigh[stringIndex] = "x";
        } else {
          stringFretsLowToHigh[stringIndex] = noteIndex;
        }
        this.props.onUpdateTabForSingleChord({ stringFretsLowToHigh }, this.props.chordIndex);
      }
    };

  handleInvalidTabIndexSelect = () => {
    // opportunity to show some UX
    console.warn("invalid note selected");
  };

  render(): ReactNode {
    const { scale, tuning, capoPosition, chord } = this.props;
    const noteInclusionMap = buildNoteInclusionMap(chord, scale);
    const noteNameMap = buildNoteNameMap(scale);
    return (
      <div className="string-instrument-neck">
        {tuning.stringsLowToHigh.map((noteIdsPerString, index) => {
          return this.renderSingleString(noteIdsPerString, index, noteInclusionMap, noteNameMap, capoPosition);
        })}
        <div className="string-instrument-guide">
          {GUIT_GUIDE_NUMS.map((n, index) => {
            return (
              <div key={"guide" + index} className="string-instrument-guide-number">
                {n}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

const GUIT_GUIDE_NUMS = ["0", " ", " ", "3", " ", "5", " ", " ", "8", " ", "10", " ", "12"];

export default StringInstrumentNeck;
